// react
import styled from 'styled-components';

import {
  Button,
  Card,
  Container,
  Divider,
  Icon,
  ProgressBar,
} from '@/components/atoms';
import { BackButton, FloatingActions } from '@/components/molecules';

// fixture
import { SuccessContent } from '@/components/organisms';

export const FinancialStatusComplete = ({
  clientName = 'Your Loved One',
  toFundingGuide,
  onPrintPdf,
  onOpenSections,
}: FinancialStatusCompleteProps) => {
  return (
    <PageWrapper>
      <TopRow>
        <BackButton isNoMargin={true} />
        <PrintWrapper>
          <Button
            ariaLabel='print'
            variant='link'
            onPress={onPrintPdf}
            iconLeft={'print'}
            size='sm'
          >
            Print
          </Button>
          <Button
            ariaLabel='sections'
            variant='link'
            onPress={onOpenSections}
            iconLeft={'list'}
            size='sm'
          >
            Sections
          </Button>
        </PrintWrapper>
      </TopRow>
      <Container size='xl'>
        <ContentWrapper>
          <ProgressHeader>{clientName}'s Financial Status</ProgressHeader>
          <ProgressBar label={'Progress'} progress={100} limit={100} />
          <Divider isOr={false} />
          <SuccessContent
            header='Financial Status'
            cardHeader='Now Review Your Answers'
            isComplete={true}
            fixtures={[
              {
                icon: 'check-circle',
                text: 'Review your Funding Consultation answers to ensure all the needs captured are correct',
                iconColor: 'success' as 'success',
              },
              {
                icon: 'user',
                text: 'Once submitted you can chat to your Care Expert regarding your Funding Consultation',
                iconColor: 'blue' as 'blue',
              },
              {
                icon: 'file-contract',
                text: 'Your Care Expert will now provide you with your Funding Consultation',
                iconColor: 'primary' as 'primary',
              },
            ]}
          />
          <FloatingActions
            direction={'col'}
            actions={[
              {
                label: 'Submit Form',
                to: toFundingGuide,
              },
            ]}
          />
        </ContentWrapper>
      </Container>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  padding: ${(props) => props.theme.spacing[24]};
  padding-bottom: ${(props) => props.theme.spacing[96]};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const PrintWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-right: ${(props) => props.theme.spacing[16]};
`;

const ProgressHeader = styled.div`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.heading['3xl']};
  text-align: center;
`;

const Header = styled(ProgressHeader)`
  color: ${(props) => props.theme.color.neutral[100]};
`;

const ContentHeader = styled(ProgressHeader)`
  text-align: left;
  color: ${(props) => props.theme.color.neutral[90]};
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${(props) => props.theme.spacing[24]};
`;

const ContentCard = styled(Card)`
  padding: ${(props) => props.theme.spacing[32]};
`;

const ColoredIcon = styled(Icon)<{
  iconColor: 'orange' | 'blue' | 'success' | 'primary';
}>`
  color: ${(props) =>
    props.iconColor === 'orange'
      ? props.theme.color.primary[30]
      : props.iconColor === 'blue'
        ? props.theme.color.actionPlanCards.blue.primary
        : props.iconColor === 'success'
          ? props.theme.color.success[40]
          : props.theme.color.primary[70]};
`;

const TextCell = styled.td`
  font: ${(props) => props.theme.typography.body.md.regular};
  padding: ${(props) => props.theme.spacing[12]} 0;
`;

const IconCell = styled.td`
  padding-right: ${(props) => props.theme.spacing[12]};
`;

interface FinancialStatusCompleteProps {
  clientName?: string;
  toFundingGuide?: string;
  onPrintPdf?: () => void;
  onOpenSections?: () => void;
}

FinancialStatusComplete.displayName = 'FinancialStatusComplete';

export default FinancialStatusComplete;
